import { styled } from '@mui/material';

import { AddIcon, LabelIcon } from '../../../icons';
import { Button } from '../../atoms/Button';
import { Checkbox } from '../../atoms/Checkbox';
import { CircularProgress } from '../../atoms/CircularProgress';
import { Divider } from '../../atoms/Divider';
import { Tag } from '../../atoms/Tag';
import { Typography } from '../../atoms/Typography';
import { Popover } from '../Popover';
import { TextField } from '../TextField';
import { type TagPickerOption, type TagPickerProps, useTagPicker } from './useTagPicker';

type Props<T extends TagPickerOption = TagPickerOption> = TagPickerProps<T>;

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const TriggerRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

const InlineTagsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  alignItems: 'center',
}));

const PopoverContent = styled('div')(({ theme }) => ({
  width: 280,
  maxHeight: 380,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const OptionsList = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  maxHeight: 220,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

const OptionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)}`,
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const OptionLabel = styled('span')(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

const CreateOptionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.primary.main,
  userSelect: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CreateOptionLabel = styled('span')({
  fontSize: '0.8125rem',
  fontWeight: 600,
});

const EmptyStateText = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: '0.8125rem',
}));

const LoadingRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const ErrorMessage = styled('div')(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  paddingInline: theme.spacing(0.5),
  marginTop: theme.spacing(-0.5),
}));

export function TagPicker<T extends TagPickerOption = TagPickerOption>(props: Props<T>) {
  const hook = useTagPicker(props);
  const showInline = props.showSelectedTagsInline ?? true;
  const tagColor = props.tagColor ?? 'default';

  return (
    <Root data-testid={props['data-testid']}>
      <TriggerRow>
        <Button
          variant="text"
          color="default"
          size="small"
          startIcon={<LabelIcon />}
          onClick={hook.handleOpen}
          disabled={props.disabled}
        >
          {props.triggerButtonText ?? 'Ajouter / modifier des tags'}
        </Button>

        {showInline && props.value.length > 0 ? (
          <InlineTagsWrapper>
            {props.value.map((tag: T) => (
              <Tag
                key={tag.id}
                label={tag.name}
                color={tag.color ?? tagColor}
                variantIcon="label"
              />
            ))}
          </InlineTagsWrapper>
        ) : null}
      </TriggerRow>

      <Popover
        open={hook.isOpen}
        anchorEl={hook.anchorEl}
        onClose={hook.handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        aria-label={props.title ?? 'Attribuer des tags'}
      >
        <PopoverContent>
          <Typography variant="body2">
            {props.title ?? 'Attribuer des tags'}
          </Typography>

          <TextField
            id={hook.searchInputId}
            value={hook.searchTerm}
            onChange={(e) => hook.handleSearchChange(e.target.value)}
            placeholder={props.searchPlaceholder ?? 'Rechercher ou créer un tag…'}
            size="small"
            fullWidth
            autoFocus
            disabled={hook.isCreating}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (hook.canCreate) {
                  hook.handleCreateOption();
                }
              }
            }}
          />

          {hook.canCreate ? (
            <CreateOptionRow
              onClick={hook.handleCreateOption}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  hook.handleCreateOption();
                }
              }}
            >
              {hook.isCreating ? (
                <CircularProgress size="small" />
              ) : (
                <AddIcon fontSize="small" />
              )}
              <CreateOptionLabel>
                Créer « {hook.trimmedSearch} »
              </CreateOptionLabel>
            </CreateOptionRow>
          ) : null}

          {hook.errorMessage ? (
            <ErrorMessage role="alert">{hook.errorMessage}</ErrorMessage>
          ) : null}

          <Divider />

          <OptionsList role="group" aria-label="Liste des tags">
            {props.loading ? (
              <LoadingRow>
                <CircularProgress size="small" />
              </LoadingRow>
            ) : hook.filteredOptions.length === 0 && !hook.canCreate ? (
              <EmptyStateText>
                {props.emptyText ?? 'Aucun tag trouvé'}
              </EmptyStateText>
            ) : (
              hook.filteredOptions.map((opt: T) => {
                const isSelected = hook.selectedIds.has(opt.id);

                return (
                  <OptionRow
                    key={opt.id}
                    onClick={() => hook.handleToggleOption(opt)}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        hook.handleToggleOption(opt);
                      }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      tabIndex={-1}
                      size="small"
                      aria-label={opt.name}
                    />
                    <OptionLabel>{opt.name}</OptionLabel>
                  </OptionRow>
                );
              })
            )}
          </OptionsList>
        </PopoverContent>
      </Popover>
    </Root>
  );
}
